'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/refs */

import { Tooltip } from '@heroui/tooltip';
import { rememberSelectedTrialPlan, TRIAL_PLAN_CODES } from '@components/Modules/PlanCards/utils/trialPlanCodes';
import HeaderButtons from '@components/shared/Header/components/HeaderButtons';
import { GridColFullWidth } from '@constants/layout';
import { ComparerType, PlanComparerType } from '@lib/sanity/queries/modules/comparer/types';
import { Color } from '@lib/sanity/sanity.types';
import Container, { getRGBA } from '@ui/Container/Container';
import IconType from '@ui/Icon/StaticIcon/IconType';
import { StaticIcon } from '@ui/Icon/StaticIcon/StaticIcon';
import PortableText from '@ui/Text/PortableText';
import Text from '@ui/Text/Text';
import { TextVariant } from '@ui/Text/constants';
import cn from '@utils/cn';
import { useRef, useState } from 'react';
import FeatureVariant from './FeatureVariant';
import ComparerHeader from './ComparerHeader';

const FEATURES_COL_WIDTH = 320;
const DEFAULT_HIGHLIGHTED_PLAN_INDEX = 2;

const COLLAPSED_HEIGHT = 800;

const ComparerModule = ({ data }: { data: ComparerType }) => {
  const [showAll, setShowAll] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const planCount = data.featuresComparer?.[0]?.plansComparer?.length ?? 0;
  const highlightedPlanIndex = data.mostPopularIndex ?? DEFAULT_HIGHLIGHTED_PLAN_INDEX;
  const headerPlans = data.featuresComparer?.find((f) => f.variant === 'header')?.plansComparer;

  const allFeatures = data.featuresComparer ?? [];

  return (
    <Container className="hidden lg:flex justify-center">
      <div id="comparer" className={cn('flex flex-col gap-8 md:gap-16 w-full', GridColFullWidth)}>
        <ComparerHeader data={data} />
        <div
          className="relative overflow-hidden rounded-3xl transition-[max-height] duration-500 ease-in-out"
          style={{
            maxHeight: showAll ? (contentRef.current?.scrollHeight ?? 'none') : COLLAPSED_HEIGHT,
          }}
        >
          <HighlightedColumnBackground
            mostPopularColor={data.mostPopularColor ?? null}
            planCount={planCount}
            highlightIndex={highlightedPlanIndex}
            featuresColWidth={FEATURES_COL_WIDTH}
          />

          <div ref={contentRef} className="relative flex flex-col">
            <div className="flex items-center">
              <div
                className="flex shrink-0 items-center justify-between px-4 py-4"
                style={{ width: FEATURES_COL_WIDTH }}
              >
                <Text
                  content="Features"
                  variant={TextVariant.TextBodySM}
                  className="font-medium leading-7 tracking-[0.15px]"
                />
              </div>
              {headerPlans?.map((plan, planIndex) => (
                <div
                  key={planIndex}
                  className="flex flex-1 items-center self-stretch"
                  data-testid="comparer-plan-header"
                >
                  <div className="flex items-center self-stretch">
                    <div className={cn('h-full w-px bg-black/8', planIndex >= highlightedPlanIndex && 'opacity-0')} />
                  </div>
                  {/* Plan content */}
                  <div className="flex items-center justify-between gap-2 px-4 py-4 w-full">
                    <PortableText
                      content={plan.title}
                      variant={TextVariant.TextBodySM}
                      className="font-medium whitespace-nowrap shrink-0"
                    />
                    <div className="hidden xl:block">
                      <PlanButton plan={plan} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-px w-full bg-black/8" />

            {allFeatures.map((feature, featureIndex) => {
              const isCategory = feature.variant === 'category';
              const isHeader = feature.variant === 'header';
              const nextFeature = allFeatures[featureIndex + 1];
              const isLastInCategory =
                nextFeature?.variant === 'category' ||
                nextFeature?.variant === 'header' ||
                featureIndex === allFeatures.length - 1;

              if (isCategory || isHeader) {
                return (
                  <div key={featureIndex}>
                    <div className="flex h-14 items-end">
                      <div className="flex h-full flex-1 items-end justify-between px-4 py-2">
                        <PortableText
                          content={feature.title}
                          variant={TextVariant.TextBodySM}
                          className="font-medium leading-7 tracking-[0.15px]"
                        />
                      </div>
                    </div>
                    <div className="h-px w-full bg-black/8" />
                  </div>
                );
              }
              return (
                <div key={featureIndex}>
                  <div className="flex items-center">
                    <div
                      className="flex shrink-0 items-center justify-between px-4 py-[9px]"
                      style={{ width: FEATURES_COL_WIDTH }}
                    >
                      <Tooltip
                        color="foreground"
                        placement="right-end"
                        className="max-w-xs rounded-lg px-3 py-2"
                        showArrow
                        closeDelay={0}
                        content={
                          <div className="flex flex-col gap-1">
                            <PortableText
                              content={feature.title}
                              variant={TextVariant.TextDescription}
                              className="font-medium text-white"
                            />
                            {feature.tooltipDescription && (
                              <PortableText
                                content={feature.tooltipDescription}
                                variant={TextVariant.TextDescription}
                                className="text-white/80"
                              />
                            )}
                          </div>
                        }
                      >
                        <span className="cursor-default">
                          <PortableText
                            content={feature.title}
                            variant={TextVariant.TextBodySM}
                            className="leading-[22px] tracking-[0.1px]"
                          />
                        </span>
                      </Tooltip>
                    </div>

                    {feature.plansComparer?.map((plan, planIndex) => (
                      <div key={planIndex} className="flex flex-1 items-center self-stretch">
                        <div className="flex items-center self-stretch">
                          <div
                            className={cn('h-full w-px bg-black/8', planIndex >= highlightedPlanIndex && 'opacity-0')}
                          />
                        </div>
                        <div className="flex min-h-px min-w-px flex-1 items-center justify-center px-4 py-2">
                          <FeatureVariant plan={plan} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {isLastInCategory && <div className="h-px w-full bg-black/8" />}
                </div>
              );
            })}
          </div>

          {allFeatures.length > 15 && (
            <div
              className={cn(
                'absolute inset-x-0 bottom-0 z-10 flex flex-col items-center pb-3 pt-14 transition-opacity duration-300',
                showAll ? 'pointer-events-none opacity-0' : 'opacity-100',
              )}
            >
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.95) 50%, white 100%)',
                }}
              />
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="relative z-10 flex items-center gap-1 rounded-full bg-black px-[22px] py-2 text-sm font-medium text-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.08)] transition-colors hover:bg-[#1a1a1a]"
              >
                <Text content="Compare All Features" variant={TextVariant.TextBodySM} className="text-white" />
                <StaticIcon icon={IconType.chevronDown} className="size-4 text-white" svgClassName="fill-white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

function HighlightedColumnBackground({
  planCount,
  highlightIndex,
  mostPopularColor,
  featuresColWidth,
}: {
  planCount: number;
  highlightIndex: number;
  mostPopularColor: Color | null;
  featuresColWidth: number;
}) {
  if (planCount === 0) return null;

  const columnWidthCalc = `(100% - ${featuresColWidth}px) / ${planCount}`;
  const leftCalc = `${featuresColWidth}px + (${highlightIndex} * ${columnWidthCalc})`;

  return (
    <div
      className="pointer-events-none absolute rounded-3xl"
      style={{
        backgroundColor: `${getRGBA(mostPopularColor)}`,
        left: `calc(${leftCalc})`,
        width: `calc(${columnWidthCalc})`,
        top: 0,
        bottom: 0,
      }}
    />
  );
}

function PlanButton({ plan }: { plan: PlanComparerType }) {
  // Same cookie the plan cards above the table write, so the app starts this column's trial.
  const handleClick = (): void => {
    if (plan.planCode) {
      rememberSelectedTrialPlan(TRIAL_PLAN_CODES[plan.planCode]);
    }
  };

  return (
    <Container removeContainer theme={plan.headerButtons?.[0]?.theme} className="w-auto">
      <div onClick={handleClick}>
        <HeaderButtons
          buttonPrimary={plan.headerButtons?.[0]?.buttonPrimary}
          buttonSecondary={plan.headerButtons?.[0]?.buttonSecondary}
          size="xs"
          className="justify-end"
        />
      </div>
    </Container>
  );
}

export default ComparerModule;
